import dynamic from 'helpers/dynamic';
import useSegmentation from 'hooks/user/useSegmentation';
import type { WebsiteIcon as WebsiteIconEnum } from 'autogen/sharedEnums';

// Payment
const Dankort = dynamic(() => import('icons/payments-providers/Dankort.svg?react'));
const Klarna = dynamic(() => import('icons/payments-providers/Klarna.svg?react'));
const Mastercard = dynamic(() => import('icons/payments-providers/Mastercard.svg?react'));
const MobilepayVertical = dynamic(() => import('icons/payments-providers/MobilepayVertical.svg?react'));
const MobilepayHorizontal = dynamic(() => import('icons/payments-providers/MobilepayHorizontal.svg?react'));
const Swish = dynamic(() => import('icons/payments-providers/Swish.svg?react'));
const Vipps = dynamic(() => import('icons/payments-providers/Vipps.svg?react'));
const Visa = dynamic(() => import('icons/payments-providers/Visa.svg?react'));
const GooglePay = dynamic(() => import('icons/payments-providers/Googlepay.svg?react'));
const ApplePay = dynamic(() => import('icons/payments-providers/Applepay.svg?react'));
const Anyday = dynamic(() => import('icons/payments-providers/Anyday.svg?react'));

// Shipping
const Bring = dynamic(() => import('icons/delivery-providers/Bring.svg?react'));
const Burd = dynamic(() => import('icons/delivery-providers/Burd.svg?react'));
const Dao = dynamic(() => import('icons/delivery-providers/Dao.svg?react'));
const GLS = dynamic(() => import('icons/delivery-providers/GLS.svg?react'));
const Instabox = dynamic(() => import('icons/delivery-providers/Instabox.svg?react'));
const Postnord = dynamic(() => import('icons/delivery-providers/Postnord.svg?react'));
const Helthjem = dynamic(() => import('icons/delivery-providers/Helthjem.svg?react'));
const Matas = dynamic(() => import('icons/delivery-providers/Matas.svg?react'));
const Kicks = dynamic(() => import('icons/delivery-providers/Kicks.svg?react'));
const Budbee = dynamic(() => import('icons/delivery-providers/Budbee.svg?react'));
const CityMail = dynamic(() => import('icons/delivery-providers/CityMail.svg?react'));
const Posti = dynamic(() => import('icons/delivery-providers/Posti.svg?react'));

// Social
const Facebook = dynamic(() => import('icons/social/Facebook.svg?react'));
const Instagram = dynamic(() => import('icons/social/Instagram.svg?react'));
const Youtube = dynamic(() => import('icons/social/Youtube.svg?react'));
const TikTok = dynamic(() => import('icons/social/TikTok.svg?react'));

// App stores
const AppStore = dynamic(() => import('icons/app-stores/AppStore.svg?react'));
const PlayStore = dynamic(() => import('icons/app-stores/PlayStore.svg?react'));

export type SupplierIconProps = {
    icon: WebsiteIconEnum;
    useVertical?: boolean;
} & React.ComponentPropsWithoutRef<'svg'>;

export default function SupplierIcon({ icon, useVertical, ...restProps }: SupplierIconProps): JSX.Element {
    const { tenant } = useSegmentation();

    switch (icon) {
        case 'AppStore_Apple':
            return <AppStore {...restProps} />;
        case 'AppStore_GooglePlay':
            return <PlayStore {...restProps} />;
        case 'Payment_Anyday':
            return <Anyday {...restProps} />;
        case 'Payment_ApplePay':
            return <ApplePay {...restProps} />;
        case 'Payment_Dankort':
            return <Dankort {...restProps} />;
        case 'Payment_GooglePay':
            return <GooglePay {...restProps} />;
        case 'Payment_Klarna':
            return <Klarna {...restProps} />;
        case 'Payment_MasterCard':
            return <Mastercard {...restProps} />;
        case 'Payment_MobilePay':
            if (useVertical) return <MobilepayVertical {...restProps} />;
            return <MobilepayHorizontal {...restProps} />;
        case 'Payment_Swish':
            return <Swish {...restProps} />;
        case 'Payment_Vipps':
            return <Vipps {...restProps} />;
        case 'Payment_Visa':
            return <Visa {...restProps} />;
        case 'Shipping_Bring':
            return <Bring {...restProps} />;
        case 'Shipping_Burd':
        case 'Shipping_BurdEconomy':
            return <Burd {...restProps} />;
        case 'Shipping_DAO':
            return <Dao {...restProps} />;
        case 'Shipping_GLS':
            return <GLS {...restProps} />;
        case 'Shipping_HeltHjem':
        case 'Shipping_HeltHjemExpress':
            return <Helthjem {...restProps} />;
        case 'Shipping_Instabox':
            return <Instabox {...restProps} />;
        case 'Shipping_PostNord':
            return <Postnord {...restProps} />;
        case 'Shipping_Matas':
            return tenant === 'Matas' ? <Matas {...restProps} /> : <Kicks {...restProps} />;
        case 'Shipping_Budbee':
            return <Budbee {...restProps} />;
        case 'Shipping_CityMail':
            return <CityMail {...restProps} />;
        case 'Shipping_Posti':
            return <Posti {...restProps} />;
        case 'Social_Facebook':
            return <Facebook {...restProps} />;
        case 'Social_Instagram':
            return <Instagram {...restProps} />;
        case 'Social_YouTube':
            return <Youtube {...restProps} />;
        case 'Social_TikTok':
            return <TikTok {...restProps} />;
        default:
            throw new Error(`Parameter type '${icon}' is not supported`);
    }
}
